import { ChangeDetectionStrategy, Component, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import {
  StylesMenuHeaderComponent,
  StylesMenuWrapperComponent,
  StylesSideMenuLabelComponent,
} from '@brafton/angular-styles-library';

import { NAV_ITEMS, NavItem } from '../../layout/shell/nav';
import { APP_VERSION } from '../../core/app-version';

/**
 * The app's nav rail: a `mat-drawer` holding the org styles library's side menu, with
 * the routed page projected into `mat-drawer-content` via `[page-content]`.
 *
 * Modeled on atlas-app-v2's `shared-side-menu`, which is the reference for consuming
 * these components. Two details of that contract matter here:
 *
 *  - `route` is handled by the header itself, via `Router.navigateByUrl`. That cannot
 *    leave the app, so external rows pass no `route` and open their URL from a click
 *    handler instead — see {@link openExternal}. That keeps every row a bare
 *    `styles-menu-header`, with no wrapper element around any of them.
 *  - Every row is a flat, top-level `styles-menu-header` with no children. The nav had
 *    one expandable group ("My Team"), which needed the header's `children` QueryList
 *    wired to each child's `visable`; with it gone, so are the `@ViewChildren` query and
 *    the `styles-menu-item` import. `styles-menu-item` is NOT an alternative for these
 *    rows: it hides itself unless a header drives its `visable`, and indents its icon
 *    50px to sit beneath a parent. A future group needs that wiring back, plus its OWN
 *    query — one shared query would make every group expand together.
 *
 * Change detection is deliberately eager: the library components mutate their own
 * `expanded` / `selected` state imperatively and expose it through plain properties, so
 * bindings that read them need a default-CD host to keep up. atlas-app-v2 does the same.
 */
@Component({
  selector: 'app-side-menu',
  imports: [
    RouterLink,
    RouterLinkActive,
    MatSidenavModule,
    StylesMenuWrapperComponent,
    StylesMenuHeaderComponent,
    StylesSideMenuLabelComponent,
  ],
  templateUrl: './side-menu.html',
  styleUrl: './side-menu.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class SideMenu {
  protected readonly items = NAV_ITEMS;

  /** Version label shown at the top of the rail; defaults to this build's version. */
  readonly appVersion = input<string>(APP_VERSION);

  /**
   * Opens an external destination in a new tab.
   *
   * `noopener` matters: without it the opened page gets a live `window.opener` handle
   * back into this app.
   */
  protected openExternal(url: string): void {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  /**
   * The routes a row highlights for: its own by default, or whatever it declares —
   * including nothing, for a row that shares a destination with another and should
   * leave the highlight to it.
   */
  protected activeRoutes(item: NavItem): string[] {
    return item.activeRoutes ?? (item.route ? [item.route] : []);
  }
}
